import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jobsEndpoint } from "../constants";
import CVLibraryLogo from "../assets/cvl-og-image.jpg";

const CVSearch = () => {
  const [candidates, setCandidates] = useState([]);
  const [searchParams, setSearchParams] = useState({
    q: "software developer",
    geo: "London",
    distance: 25,
    salary: 5,
    perpage: 10,
    offset: 0
  });
  const [searchResults, setSearchResults] = useState({
    matches_total: 0,
    matches_returned: 0,
    register_url: null,
    has_client_access: false,
    endpoint_used: null,
    session_id: null
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const searchCandidates = async (params = searchParams) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post(jobsEndpoint+'cv-library/search/', params, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        console.log(response.data)
        setCandidates(response.data.results.candidates || []);
        setSearchResults({
          matches_total: response.data.results.matches_total || 0,
          matches_returned: response.data.results.matches_returned || 0,
          register_url: response.data.results.register_url,
          has_client_access: response.data.has_client_access || false,
          endpoint_used: response.data.endpoint_used,
          session_id: response.data.session_id
        });
      }
    } catch (err) {
      setError("Failed to search candidates.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const unlockCandidate = async (recordId) => {
    try {
      setLoading(true);
      
      const response = await axios.post(
        jobsEndpoint + 'cv-library/candidate/full-view/',
        { 
          record_id: recordId, 
          session_id: searchResults.session_id,
          html_preview: 1,
          encode: 1 
        },
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // Update session ID for future requests
        setSearchResults(prev => ({
          ...prev,
          session_id: response.data.session_id
        }));
        
        // Navigate to full candidate view
        navigate(`/cv-details/${recordId}?full=true`);
      }
    } catch (err) {
      console.error("Failed to unlock candidate:", err);
      setError("Failed to unlock candidate details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    searchCandidates();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const updatedParams = { ...searchParams, offset: 0 };
    setSearchParams(updatedParams);
    searchCandidates(updatedParams);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const toggleDropdown = (candidateId) => {
    setDropdownOpen(dropdownOpen === candidateId ? null : candidateId);
  };

  const handleViewCandidate = (candidateId) => {
    navigate(`/cv-details/${candidateId}`);
    setDropdownOpen(null);
  };

  const handleUnlockCandidate = (recordId = null) => {
    if (searchResults.has_client_access && recordId) {
      // User has client access - unlock directly
      unlockCandidate(recordId);
    } else if (searchResults.register_url) {
      // Non-client user - redirect to registration
      window.open(searchResults.register_url, '_blank');
    }
    setDropdownOpen(null);
  };

  const handlePagination = (direction) => {
    const newOffset = direction === 'next' 
      ? searchParams.offset + searchParams.perpage 
      : Math.max(0, searchParams.offset - searchParams.perpage);
    
    const updatedParams = { ...searchParams, offset: newOffset };
    setSearchParams(updatedParams);
    searchCandidates(updatedParams);
  };

  const sortedCandidates = useMemo(() => {
    let result = [...candidates];

    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';
        
        if (sortConfig.key === 'relevance') {
          aVal = parseInt(aVal) || 0;
          bVal = parseInt(bVal) || 0;
        }
        
        if (aVal < bVal) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [candidates, sortConfig]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest('.dropdown-menu')) {
        return;
      }
      setDropdownOpen(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold text-gray-900">CV Search</h1>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">powered by</span>
                <img 
                  src={CVLibraryLogo} 
                  alt="CV Library" 
                  className="h-8 w-auto object-contain"
                />
              </div>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              Search and discover candidates from CV-Library.
              {searchResults.has_client_access && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Full Access
                </span>
              )}
            </p>
          </div>
          
          {!searchResults.has_client_access && searchResults.register_url && (
            <button
              onClick={() => handleUnlockCandidate()}
              className="px-4 py-2 text-sm text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6]"
            >
              Sign up with CV Library for Full Access
            </button>
          )}
        </div>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
            <input
              type="text"
              name="q"
              value={searchParams.q}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#19bfb7]"
              placeholder="e.g. software developer"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              name="geo"
              value={searchParams.geo}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#19bfb7]"
              placeholder="e.g. London"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Distance (miles)</label>
            <select
              name="distance"
              value={searchParams.distance}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#19bfb7]"
            >
              <option value={10}>10 miles</option>
              <option value={25}>25 miles</option>
              <option value={50}>50 miles</option>
              <option value={100}>100 miles</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Results per page</label>
            <select
              name="perpage"
              value={searchParams.perpage}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#19bfb7]"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6] disabled:opacity-50"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {/* Loading/Error */}
      {loading && <p className="text-gray-500">Searching candidates...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {/* Results Summary */}
      {!loading && !error && searchResults.matches_total > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <div className="pt-3 px-5">
            <div className="flex justify-between items-start">
              <h3 className="text-sm font-semibold text-gray-900">Search Results</h3>
              <div className="text-xs text-gray-500">
                Using {searchResults.endpoint_used} endpoint
              </div>
            </div>
          </div>
          <div className="flex gap-3 items-end py-4 px-5 border-b border-gray-200">
            <h3 className="text-2xl text-gray-900 font-semibold">
              {searchResults.matches_total}
            </h3>
            <div className="flex items-center gap-1 text-sm text-blue-600">
              <span>matches found</span>
              <span className="text-gray-600 ml-1">showing {searchResults.matches_returned}</span>
            </div>
          </div>
        </div>
      )}

      {/* Candidates Table */}
      {!loading && !error && sortedCandidates.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-5 py-3 grid md:grid-cols-8 grid-cols-5 border-b border-gray-200">
            <div className="flex gap-3 items-center md:col-span-3 col-span-2">
              <input type="checkbox" className="rounded border-gray-300" />
              <h5 className="text-gray-500 font-semibold cursor-pointer" onClick={() => handleSort('name')}>
                Candidate {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </h5>
            </div>
            <div className="grid md:grid-cols-5 grid-cols-3 md:col-span-5 col-span-3">
              <h5 className="text-gray-500 font-semibold text-xs cursor-pointer" onClick={() => handleSort('current_job_title')}>
                Current Role {sortConfig.key === 'current_job_title' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </h5>
              <h5 className="text-gray-500 font-semibold text-xs cursor-pointer hidden md:block" onClick={() => handleSort('expected_salary')}>
                Expected Salary {sortConfig.key === 'expected_salary' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </h5>
              <h5 className="text-gray-500 font-semibold text-xs cursor-pointer" onClick={() => handleSort('relevance')}>
                Relevance {sortConfig.key === 'relevance' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </h5>
              <h5 className="text-gray-500 font-semibold text-xs">Location</h5>
              <h5 className="text-gray-500 font-semibold text-xs hidden md:block cursor-pointer" onClick={() => handleSort('last_modified')}>
                Last Updated {sortConfig.key === 'last_modified' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </h5>
            </div>
          </div>

          {sortedCandidates.map((candidate) => (
            <div key={candidate.record_id} className="grid md:grid-cols-8 grid-cols-5 items-center p-4 border-b border-gray-200 hover:bg-gray-50">
              <div className="flex items-center gap-3 md:col-span-3 col-span-2">
                <input type="checkbox" className="rounded border-gray-300" />
                <div>
                  <button
                    onClick={() => handleViewCandidate(candidate.record_id)}
                    className="md:font-medium font-bold text-xs text-[#19bfb7] hover:text-[#18ada6] text-left"
                  >
                    {candidate.name || 'Hidden'}
                  </button>
                  <p className="text-xs text-gray-500">{candidate.desired_job_title}</p>
                </div>
              </div>
              <div>
                <p className="font-medium text-xs text-gray-500">{candidate.current_job_title || '--'}</p>
              </div>
              <div className="hidden md:block">
                <p className="font-medium text-xs text-gray-500">{candidate.expected_salary || '--'}</p>
              </div>
              <div>
                <span className="text-center text-[12px] border py-1 px-2 rounded-md bg-blue-100 text-blue-600 border-blue-400">
                  {candidate.relevance}%
                </span>
              </div>
              <div>
                <p className="font-medium text-xs text-gray-500">
                  {candidate.town}
                  {candidate.distance && <span className="block text-xs text-gray-400">{candidate.distance}</span>}
                </p>
              </div>
              <div className="hidden md:flex justify-between items-center">
                <span className="font-medium text-xs text-gray-500">
                  {candidate.last_modified ? new Date(candidate.last_modified.split(' ')[0].split('/').reverse().join('-')).toLocaleDateString() : '--'}
                </span>
                <div className="relative">
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDropdown(candidate.record_id);
                    }}
                    className="text-gray-600 cursor-pointer text-xl font-bold xl:ml-10"
                  >
                    &#8942;
                  </span>
                  {dropdownOpen === candidate.record_id && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-10 dropdown-menu">
                      <div className="py-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCandidate(candidate.record_id);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          View Details
                        </button>
                        {searchResults.has_client_access ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnlockCandidate(candidate.record_id);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-[#19bfb7] hover:bg-gray-100"
                          >
                            Unlock Full Profile
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnlockCandidate();
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-[#19bfb7] hover:bg-gray-100"
                          >
                            Sign up with CV Library to unlock
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Mobile dropdown */}
              <div className="md:hidden relative">
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDropdown(candidate.record_id);
                  }}
                  className="text-gray-600 cursor-pointer text-xl font-bold"
                >
                  &#8942;
                </span>
                {dropdownOpen === candidate.record_id && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-10 dropdown-menu">
                    <div className="py-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewCandidate(candidate.record_id);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        View Details
                      </button>
                      {searchResults.has_client_access ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnlockCandidate(candidate.record_id);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-[#19bfb7] hover:bg-gray-100"
                        >
                          Unlock Full Profile
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnlockCandidate();
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-[#19bfb7] hover:bg-gray-100"
                        >
                          Sign up with CV Library to unlock
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Pagination */}
          <div className="flex justify-between items-center p-4">
            <button
              onClick={() => handlePagination('prev')}
              disabled={searchParams.offset === 0}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            
            <span className="text-sm text-gray-600">
              Showing {searchParams.offset + 1} - {Math.min(searchParams.offset + searchParams.perpage, searchResults.matches_total)} of {searchResults.matches_total}
            </span>
            
            <button
              onClick={() => handlePagination('next')}
              disabled={searchParams.offset + searchParams.perpage >= searchResults.matches_total}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* No Results */}
      {!loading && !error && sortedCandidates.length === 0 && searchResults.matches_total === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-500">No candidates found. Try adjusting your search criteria.</p>
        </div>
      )}
    </div>
  );
};

export default CVSearch;